/**
 * MCP Connection Editing — pure functions for create/edit-flow logic (spec §70 §6, §4.1)
 *
 * Extracted from Create/EditMcpConnectionModal so the domain logic can be
 * unit-tested independently of React rendering.
 *
 * v2: All workspace entries use workspace-secret-v2 (Spec §4.1).
 */

import type { McpConnectionEntry, McpWorkspaceConfig } from './types'
import { generateRandomKey } from './crypto'
import { decodeWorkspaceSecretV2, encodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'

// ── Types ──

export interface RegistryEntry {
    blobId: string
    label: string
    emoji?: string
    capability: string
}

export interface EditResult {
    configs: McpWorkspaceConfig[]
    mcpReaderSeeds: Record<string, string>
    mcpWriterSeeds: Record<string, string>
}

export interface BuildEditPayloadOptions {
    requireDedicatedReaderSeed?: boolean
}

export function requiresConnectionRecreation(connection: McpConnectionEntry): boolean {
    const readerSeeds = connection.mcpReaderSeeds ?? {}
    return connection.workspaceBlobIds.some(blobId => !readerSeeds[blobId])
}

// ── Alias generation (spec §6.2, §9.1) ──

/**
 * Slugify a workspace label into a short alias suitable for vpath references.
 * Strips diacritics, lowercases, replaces non-alphanumeric runs with hyphens,
 * and truncates to 32 chars.
 */
export function slugifyAlias(label: string): string {
    return label
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip diacritics
        .replace(/[^a-z0-9]+/g, '-')                      // non-alphanumeric → hyphen
        .replace(/^-+|-+$/g, '')                           // trim edges
        .slice(0, 32)
        || 'workspace'                                     // fallback for empty/symbols-only
}

/**
 * Deduplicate aliases by appending -2, -3, etc. for collisions.
 * Mutates the configs array in place.
 *
 * Correctness: reserves ALL base aliases first, then deduplicates
 * only the duplicate entries — skipping any candidate that collides
 * with a pre-existing base alias from another workspace.
 */
function deduplicateAliases(configs: McpWorkspaceConfig[]): void {
    // First pass: identify first-occurrence (kept) vs duplicate aliases
    const taken = new Map<string, number>() // alias → index of first occurrence
    const needsRename: number[] = []

    for (let i = 0; i < configs.length; i++) {
        const alias = configs[i].alias
        if (!alias) continue
        if (!taken.has(alias)) {
            taken.set(alias, i) // First occurrence keeps the base alias
        } else {
            needsRename.push(i) // Subsequent occurrences need renaming
        }
    }

    // Second pass: rename duplicates, avoiding ALL reserved aliases
    for (const i of needsRename) {
        const base = configs[i].alias!
        let suffix = 2
        while (taken.has(`${base}-${suffix}`)) suffix++
        configs[i].alias = `${base}-${suffix}`
        taken.set(configs[i].alias!, i)
    }
}

// ── Pure functions ──

/**
 * Detect workspaces in the connection that are no longer in the user's registry.
 * These are "orphaned" — the user lost access (e.g. shared access revoked, workspace deleted).
 */
export function detectOrphanedWorkspaces(
    connectionBlobIds: string[],
    registryEntries: RegistryEntry[],
): string[] {
    const registryIds = new Set(registryEntries.map(e => e.blobId))
    return connectionBlobIds.filter(id => !registryIds.has(id))
}

/**
 * Build workspace configs and manage writer seeds for an edit operation.
 *
 * Handles all edge cases:
 *  - Reuses existing MCP writer seed when write access is preserved
 *  - Generates new seed when write access is added for the first time
 *  - Removes seed when write access is revoked
 *  - Cleans up stale seeds for workspaces removed from selection
 *  - Skips workspaces with missing entries or readKeys
 *  - Generates deduplicated aliases from workspace labels (spec §9.1)
 */
export function buildEditPayload(
    selectedBlobIds: Set<string>,
    writeAccess: Set<string>,
    registryEntries: RegistryEntry[],
    existingReaderSeeds: Record<string, string>,
    existingSeeds: Record<string, string>,
    options: BuildEditPayloadOptions = {},
): EditResult {
    const configs: McpWorkspaceConfig[] = []
    const mcpReaderSeeds: Record<string, string> = { ...existingReaderSeeds }
    const mcpWriterSeeds: Record<string, string> = { ...existingSeeds }
    const requireDedicatedReaderSeed = options.requireDedicatedReaderSeed ?? false

    for (const blobId of selectedBlobIds) {
        const entry = registryEntries.find(e => e.blobId === blobId)
        if (!entry) continue

        // Decode user's workspace-secret-v2 (Spec §4.1 — only v2 accepted per §1.2)
        const decoded = decodeWorkspaceSecretV2(entry.capability)
        if (!decoded) continue

        const hasWrite = writeAccess.has(blobId)
        const userWriteSeed = decoded.writerSeed ?? null
        const existingReaderSeed = mcpReaderSeeds[blobId]
        let readerSeed = existingReaderSeed ?? decoded.readerSeed
        let wsSecret: string

        if (!existingReaderSeed && requireDedicatedReaderSeed) {
            if (!userWriteSeed) {
                throw new Error(`Workspace '${entry.label}' cannot be used in an MCP connection without writer/manage access.`)
            }
            readerSeed = generateRandomKey(32)
            mcpReaderSeeds[blobId] = readerSeed
        } else if (existingReaderSeed) {
            mcpReaderSeeds[blobId] = existingReaderSeed
        }

        if (hasWrite && userWriteSeed) {
            const existingSeed = mcpWriterSeeds[blobId]
            const mcpSeed = existingSeed ?? generateRandomKey(32)
            mcpWriterSeeds[blobId] = mcpSeed
            // Create v2 workspace-secret with user's readKey + readerSeed + MCP's writerSeed
            wsSecret = encodeWorkspaceSecretV2({
                v: 2,
                type: 'workspace-secret',
                blobId,
                readKey: decoded.readKey,
                readerSeed,
                writerSeed: mcpSeed,
            })
        } else {
            delete mcpWriterSeeds[blobId]
            // Read-only: redact writerSeed from user's workspace secret
            wsSecret = encodeWorkspaceSecretV2({
                v: 2,
                type: 'workspace-secret',
                blobId,
                readKey: decoded.readKey,
                readerSeed,
            })
        }

        configs.push({
            blobId,
            alias: slugifyAlias(entry.label),
            title: entry.label,
            emoji: entry.emoji,
            workspaceSecret: wsSecret,
            permissions: {
                read: true,
                write: hasWrite && !!userWriteSeed,
                delete: hasWrite && !!userWriteSeed,
                rename: hasWrite && !!userWriteSeed,
                allowBinaryRead: true,
            },
        })
    }

    deduplicateAliases(configs)

    // Clean up stale seeds for deselected workspaces
    for (const blobId of Object.keys(mcpReaderSeeds)) {
        if (!selectedBlobIds.has(blobId)) {
            delete mcpReaderSeeds[blobId]
        }
    }
    for (const blobId of Object.keys(mcpWriterSeeds)) {
        if (!selectedBlobIds.has(blobId)) {
            delete mcpWriterSeeds[blobId]
        }
    }

    return { configs, mcpReaderSeeds, mcpWriterSeeds }
}

/**
 * Determine whether the edit form has changes relative to the original connection.
 * Tracks label, workspace selection, AND write permission changes.
 */
export function hasConnectionChanges(
    label: string,
    selectedBlobIds: Set<string>,
    writeAccess: Set<string>,
    connection: McpConnectionEntry,
): boolean {
    if (label !== connection.label) return true

    const currentWs = [...selectedBlobIds].sort().join(',')
    const originalWs = [...connection.workspaceBlobIds].sort().join(',')
    if (currentWs !== originalWs) return true

    const currentWrite = [...writeAccess].sort().join(',')
    const originalWrite = [...Object.keys(connection.mcpWriterSeeds ?? {})].sort().join(',')
    if (currentWrite !== originalWrite) return true

    return false
}

/**
 * Build workspace configs and writer seeds for a new connection.
 * Thin wrapper around buildEditPayload with empty existing seeds.
 */
export function buildCreatePayload(
    selectedBlobIds: Set<string>,
    writeAccess: Set<string>,
    registryEntries: RegistryEntry[],
): EditResult {
    return buildEditPayload(
        selectedBlobIds,
        writeAccess,
        registryEntries,
        {},
        {},
        { requireDedicatedReaderSeed: true },
    )
}
